import { useQueryClient, UseQueryOptions } from "react-query";
import { useModelService } from "@shared/services/api/hooks/useModelService";
import UserBasedVenueRepository from "../../repositories/UserBasedVenueRepository";
import { UserBasedVenueSerializer } from "../../types/user-venue";
import type {
  CreateChildVenuePayload,
  CreateChildVenueResponse,
} from "../../types/venues";

const useUserBasedVenueService = () => {
  const repository = new UserBasedVenueRepository();
  const queryClient = useQueryClient();

  const {
    useList,
    useCreate,
    useRetrieve,
    useUpdate,
    useGenericQuery,
    useGenericMutation,
    invalidateAllRepositoryQueries,
    invalidateSubcategoryQueries,
    queryKeyManager,
  } = useModelService(repository);

  const useCurrentVenueSession = ({
    options,
  }: {
    options?: UseQueryOptions<UserBasedVenueSerializer>;
  } = {}) => {
    return useGenericQuery<UserBasedVenueSerializer>("currentVenueSession", {
      options: {
        ...options,
      },
      config: {
        // Prevent unauthorized toast errors from popping up on desktop login page.
        showToastOnError: false,
      },
    });
  };

  const useChangeVenueSession = (id: number) => {
    return useGenericMutation<
      UserBasedVenueSerializer,
      Partial<UserBasedVenueSerializer>
    >("changeVenueSession", "PUT", {
      injects: { id },
    });
  };

  const useMutateUserVenueSession = ({
    options,
  }: {
    options?: UseQueryOptions;
  } = {}) => {
    return useUpdate({
      options: {
        onSuccess: (res) => {
          invalidateAllRepositoryQueries();
          options?.onSuccess?.(res);
        },
      },
    });
  };

  const useCreateChildVenue = (venueId: number) =>
    useGenericMutation<CreateChildVenueResponse, CreateChildVenuePayload>(
      "createChildVenue",
      "POST",
      { injects: { id: venueId } }
    );

  const setCurrentVenueSessionData = (
    updater: (
      prev: UserBasedVenueSerializer | undefined
    ) => UserBasedVenueSerializer | undefined
  ): void => {
    const key = queryKeyManager.generateKey(["currentVenueSession"]);
    queryClient.setQueryData(key, (prev: unknown) =>
      updater(prev as UserBasedVenueSerializer | undefined)
    );
  };

  return {
    useList,
    useCreate,
    useRetrieve,
    useUpdate,
    useChangeVenueSession,
    useCreateChildVenue,
    useCurrentVenueSession,
    useMutateUserVenueSession,
    setCurrentVenueSessionData,
    invalidateAllRepositoryQueries,
    invalidateSubcategoryQueries,
  };
};

export default useUserBasedVenueService;
